import {
  useAcceptCircuitToDistribute,
  useGetUndistributedCircuits,
  useWithdrawCircuit,
} from '@/hooks/useCircuits';
import useTableControls from '@/hooks/useTableControls';
import StyledTable from '../styled/StyledTable';
import {
  DistributorCircuit,
  distributorCircuitColumns,
} from '@/types/circuits/circuitColumnDef';
import StyledNextPreviousButtons from '../styled/StyledNextPreviousButtons';
import { useToast } from '@/hooks/useToast';
import { PAGE_SIZE } from '@/constants/pageSize';
import { useNavigate } from 'react-router';
import { PATHS } from '@/constants/Paths';
import { Dispatch, SetStateAction } from 'react';
import { Row, RowSelectionState } from '@tanstack/react-table';
import { useTranslation } from '@/i18n';
import { useTranslatedConst } from '@/hooks/useTranslatedConst';
import type { DistributorCircuitsListUrlState } from '@/utils/searchParams/buildDistributorCircuitsListSearch';

interface UndistributedCircuitsListProps {
  filters: {
    name: string;
    countries: string;
    durationDays: string;
    durationNights: string;
  };
  setRowSelection: Dispatch<SetStateAction<RowSelectionState>>;
  onlyRefused?: boolean;
  listSearchParams?: DistributorCircuitsListUrlState;
}

const UndistributedCircuitsList = ({
  filters,
  setRowSelection,
  onlyRefused = false,
  listSearchParams,
}: UndistributedCircuitsListProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const getTranslatedConst = useTranslatedConst();
  const { page, setPage, sorting, setSorting, sortField, sortOrder } =
    useTableControls();
  const { data: undistributedCircuits, isSuccess } =
    useGetUndistributedCircuits({
      page,
      pageSize: PAGE_SIZE,
      sortField,
      sortOrder,
      filters,
      onlyRefused,
    });

  const { mutateAsync: distributeCircuit } = useAcceptCircuitToDistribute();
  const { mutateAsync: refuseDistributingCircuit } = useWithdrawCircuit();

  const goToCircuit = (row: Row<DistributorCircuit>) => {
    navigate(
      PATHS.distributors.circuits.details.replace(
        ':circuitId',
        row.original.id,
      ),
      listSearchParams ? { state: { listSearchParams } } : undefined,
    );
  };

  const { toastAsync } = useToast();

  return (
    <div className="flex flex-col gap-4">
      <StyledTable
        columns={getTranslatedConst(distributorCircuitColumns)}
        data={undistributedCircuits?.circuits ?? []}
        getRowId={(row) => row.id}
        sorting={sorting}
        setSorting={setSorting}
        acceptRefuseEnablePredicate={() => !onlyRefused}
        onRowClick={goToCircuit}
        onViewRow={goToCircuit}
        onAcceptRow={(row) => {
          toastAsync(distributeCircuit([row.original.id]), {
            success: t('distributorCircuits.list.circuitDistributedSuccess'),
          });
        }}
        onRefuseRow={(row) => {
          toastAsync(refuseDistributingCircuit(row.original.id), {
            success: t('distributorCircuits.list.circuitRefusedSuccess'),
          });
        }}
        setSelection={setRowSelection}
        onRestoreRow={
          onlyRefused
            ? (row) => {
                toastAsync(distributeCircuit([row.original.id]), {
                  success: t('distributorCircuits.list.circuitRestoredSuccess'),
                });
              }
            : undefined
        }
      />
      {isSuccess && (
        <StyledNextPreviousButtons
          page={page}
          pageSize={PAGE_SIZE}
          setPage={setPage}
          dataLength={undistributedCircuits.total}
          label={t('distributorCircuits.list.circuit')}
        />
      )}
    </div>
  );
};

export default UndistributedCircuitsList;
